/**
 * Where a project stands in AI tracking setup, and whether the welcome modal is still owed.
 *
 * Nothing about progress is stored: the three steps are read back off the project itself, so a
 * project configured before this flow existed is already "done" and a project set up by hand
 * halfway through counts exactly as far as it got. The only thing persisted is whether the user
 * has been offered the flow, because that is the one fact the project cannot answer.
 */

export const WELCOME_DISMISSED_STORAGE_KEY = 'ai_tracking_onboarding_welcome_dismissed';

export const STEP_BRAND = 1;
export const STEP_PROFILE = 2;
export const STEP_PROMPTS = 3;
export const STEP_DONE = 4;
export const TOTAL_STEPS = 3;

/**
 * The three steps for one project, plus the first one still outstanding.
 *
 * `nextStep` is the step the flow should open at, and is null once there is nothing left -- which
 * is also what hides the checklist.
 */
export function getProjectSetupState(project) {
    const brand = !!(project?.own_domain && project?.own_product_name);
    const profile = !!project?.brand_profile_available;
    const prompts = (project?.prompts?.length ?? 0) > 0;

    const steps = { [STEP_BRAND]: brand, [STEP_PROFILE]: profile, [STEP_PROMPTS]: prompts };
    const doneCount = [brand, profile, prompts].filter(Boolean).length;
    const nextStep = [STEP_BRAND, STEP_PROFILE, STEP_PROMPTS].find((step) => !steps[step]) ?? null;

    return {
        brandDone: brand,
        profileDone: profile,
        promptsDone: prompts,
        steps,
        doneCount,
        totalSteps: TOTAL_STEPS,
        isComplete: nextStep === null,
        nextStep,
    };
}

export function isProjectSetupComplete(project) {
    return getProjectSetupState(project).isComplete;
}

/** Projects worth onboarding: a disabled one is not something to send the user into. */
function candidates(projects) {
    return (projects ?? []).filter((project) => project && !project.is_disabled);
}

/**
 * The project the flow should act on: the first unfinished one, default project first.
 *
 * A brand new account has exactly one project called "Default", so this is almost always that --
 * the ordering only matters for an account that has since added more.
 */
export function pickOnboardingProject(projects) {
    const unfinished = candidates(projects).filter((project) => !isProjectSetupComplete(project));
    if (unfinished.length === 0) return null;

    return unfinished.find((project) => project.is_default) ?? unfinished[0];
}

/** True once any project has been taken all the way through, which retires the welcome modal. */
export function hasConfiguredProject(projects) {
    return candidates(projects).some(isProjectSetupComplete);
}

/** Has this user already been offered (and dismissed) the welcome modal? */
export function isWelcomeDismissed() {
    try {
        const stored = JSON.parse(localStorage.getItem(WELCOME_DISMISSED_STORAGE_KEY));
        return !!stored?.[window.info?.user];
    } catch (e) {
        console.log('e', e);
        return false;
    }
}

/**
 * Remember that the user has seen the welcome modal, scoped to their account.
 *
 * Keyed by user like the country/language store, so a shared browser does not skip the flow for
 * the next person to sign in.
 */
export function dismissWelcome() {
    try {
        const existing = JSON.parse(localStorage.getItem(WELCOME_DISMISSED_STORAGE_KEY)) ?? {};
        existing[window.info?.user] = true;
        localStorage.setItem(WELCOME_DISMISSED_STORAGE_KEY, JSON.stringify(existing));
    } catch (e) {
        console.log('e', e);
    }
}

/**
 * Whether to greet this user with the welcome modal.
 *
 * It is offered once, and only to someone who has never finished a project: an existing user who
 * adds a second, empty project has already been through this and must not be greeted again.
 */
export function shouldShowWelcome(projects) {
    if (isWelcomeDismissed()) return false;
    if (hasConfiguredProject(projects)) return false;

    return pickOnboardingProject(projects) !== null;
}
